import { useSearchParams } from "@faire/prototype-playground/hooks/useSearchParams";
import { useCallback } from "react";

import type {
  PrePackAllItemsAffected,
  PrePackReportContext,
} from "./retailer/prePackPolicyData";

export const SURFACE_BRAND_PORTAL = 1;
export const SURFACE_RETAILER = 2;
export const SURFACE_PRE_PACK_POLICY = 4;
export const SURFACE_BRAND_PORTAL_ACCOUNT_SETTINGS = 5;
export const SURFACE_RETAILER_RETURN_REQUEST = 6;

export const RETAILER_SCREEN_RESOLUTION = 1;
export const RETAILER_SCREEN_REVIEW = 2;
export const RETAILER_SCREEN_RECEIVED = 3;
export const RETAILER_SCREEN_PREPACK_AFFECTED = 4;
export const RETAILER_SCREEN_SELECT_PRODUCTS = 5;

export const PREPACK_ALL_ITEMS_AFFECTED_PARAM = "prePackAllItemsAffected";
export const RETURNS_REQUIRED_PARAM = "returnsRequired";

const parsePrePackAllItemsAffected = (
  value: string | null
): PrePackAllItemsAffected => (value === "no" ? "no" : "yes");

const parseReturnsRequired = (value: string | null): boolean => value === "yes";

export const usePrototypeVariants = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const surface = Number(searchParams.get("surface") ?? SURFACE_BRAND_PORTAL);
  const retailerScreen = Number(
    searchParams.get("retailerScreen") ??
      (surface === SURFACE_PRE_PACK_POLICY
        ? RETAILER_SCREEN_PREPACK_AFFECTED
        : surface === SURFACE_RETAILER_RETURN_REQUEST
          ? RETAILER_SCREEN_SELECT_PRODUCTS
          : RETAILER_SCREEN_RESOLUTION)
  );
  const prePackAllItemsAffected = parsePrePackAllItemsAffected(
    searchParams.get(PREPACK_ALL_ITEMS_AFFECTED_PARAM)
  );
  const isReturnsRequired = parseReturnsRequired(
    searchParams.get(RETURNS_REQUIRED_PARAM)
  );

  const setSurface = useCallback(
    (nextSurface: number) => {
      setSearchParams((params) => {
        const next = new URLSearchParams(params);
        next.set("surface", String(nextSurface));

        if (nextSurface === SURFACE_PRE_PACK_POLICY) {
          next.set("retailerScreen", String(RETAILER_SCREEN_PREPACK_AFFECTED));
          next.set(PREPACK_ALL_ITEMS_AFFECTED_PARAM, "yes");
        } else if (nextSurface === SURFACE_RETAILER) {
          next.set("retailerScreen", String(RETAILER_SCREEN_RESOLUTION));
          next.delete(PREPACK_ALL_ITEMS_AFFECTED_PARAM);
        } else if (nextSurface === SURFACE_RETAILER_RETURN_REQUEST) {
          next.set("retailerScreen", String(RETAILER_SCREEN_SELECT_PRODUCTS));
          next.delete(PREPACK_ALL_ITEMS_AFFECTED_PARAM);
        } else {
          next.delete("retailerScreen");
          next.delete(PREPACK_ALL_ITEMS_AFFECTED_PARAM);
        }

        return next;
      });
    },
    [setSearchParams]
  );

  const setRetailerScreen = useCallback(
    (nextScreen: number) => {
      setSearchParams((params) => {
        const next = new URLSearchParams(params);
        next.set("retailerScreen", String(nextScreen));
        return next;
      });
    },
    [setSearchParams]
  );

  const setReturnsRequired = useCallback(
    (returnsRequired: boolean) => {
      setSearchParams((params) => {
        const next = new URLSearchParams(params);
        if (returnsRequired) {
          next.set(RETURNS_REQUIRED_PARAM, "yes");
        } else {
          next.delete(RETURNS_REQUIRED_PARAM);
        }
        return next;
      });
    },
    [setSearchParams]
  );

  const setPrePackAllItemsAffected = useCallback(
    (allItemsAffected: PrePackAllItemsAffected) => {
      setSearchParams((params) => {
        const next = new URLSearchParams(params);
        next.set(PREPACK_ALL_ITEMS_AFFECTED_PARAM, allItemsAffected);
        return next;
      });
    },
    [setSearchParams]
  );

  const continueFromPrePackAffected = useCallback(
    (context: PrePackReportContext) => {
      setSearchParams((params) => {
        const next = new URLSearchParams(params);
        next.set("retailerScreen", String(RETAILER_SCREEN_RESOLUTION));
        next.set(PREPACK_ALL_ITEMS_AFFECTED_PARAM, context.allItemsAffected);
        return next;
      });
    },
    [setSearchParams]
  );

  return {
    surface,
    retailerScreen,
    prePackAllItemsAffected,
    isReturnsRequired,
    setSurface,
    setRetailerScreen,
    setPrePackAllItemsAffected,
    setReturnsRequired,
    continueFromPrePackAffected,
    isBrandPortalIssues: surface === SURFACE_BRAND_PORTAL,
    isBrandPortal:
      surface === SURFACE_BRAND_PORTAL ||
      surface === SURFACE_BRAND_PORTAL_ACCOUNT_SETTINGS,
    isAccountSettings: surface === SURFACE_BRAND_PORTAL_ACCOUNT_SETTINGS,
    isPrePackPolicy: surface === SURFACE_PRE_PACK_POLICY,
    isReturnRequest: surface === SURFACE_RETAILER_RETURN_REQUEST,
    isRetailer:
      surface === SURFACE_RETAILER ||
      surface === SURFACE_PRE_PACK_POLICY ||
      surface === SURFACE_RETAILER_RETURN_REQUEST,
  };
};
